class MiniCart {
    constructor() {
        // Core elements
        this.body = document.body;
        this.drawer = document.getElementById('cart-drawer');
        this.drawerContent = this.drawer?.querySelector('.cart-drawer__content');
        this.cartItems = this.drawer?.querySelector('.cart-items');
        this.cartLinks = document.querySelectorAll('a[href="/cart"]');
        
        // State management
        this.isUpdating = false;
        this.changeQueue = new Set();
        this.changeDebounceTimer = null;
        this.quantityUpdateTimer = null;
        
        console.log('MiniCart initialized:', {
            hasDrawer: !!this.drawer,
            hasContent: !!this.drawerContent,
            hasItems: !!this.cartItems,
            cartLinkCount: this.cartLinks.length
        });

        if (this.drawer) {
            this.init();
        }
    }

    init() {
        this.bindEvents();
        console.log('MiniCart events bound');
    }

    bindEvents() {
        // Cart toggle events
        this.cartLinks.forEach(link => {
            link.addEventListener('click', (e) => {
                e.preventDefault();
                this.openDrawer();
            });
        });

        // Drawer events
        if (this.drawerContent) {
            this.drawerContent.addEventListener('click', (e) => {
                const closeButton = e.target.closest('.close-button');
                if (closeButton) {
                    e.preventDefault();
                    this.closeDrawer();
                }
            });
        }

        // Cart item events
        if (this.cartItems) {
            this.cartItems.addEventListener('click', this.handleCartItemClick.bind(this));
            this.cartItems.addEventListener('change', this.handleCartItemChange.bind(this));
        }

        // Global events
        document.addEventListener('click', this.handleOutsideClick.bind(this));
        window.addEventListener('cart:open', () => this.openDrawer());
        window.addEventListener('cart:close', () => this.closeDrawer());
        window.addEventListener('cart:update', () => this.refreshCart());

        console.log('All event listeners bound successfully');
    }

    handleCartItemClick(e) {
        const quantityButton = e.target.closest('.quantity__button');
        if (!quantityButton) return;

        const item = quantityButton.closest('.cart-item');
        if (!item) return;

        const quantityInput = item.querySelector('.quantity__input');
        if (!quantityInput) return;

        const currentQty = parseInt(quantityInput.value) || 0;
        const newQty = quantityButton.name === 'plus' ? currentQty + 1 : Math.max(0, currentQty - 1);

        // Update the input value immediately
        quantityInput.value = newQty;
        item.dataset.quantity = newQty;

        console.log('Quantity button clicked:', {
            button: quantityButton.name,
            itemId: item.dataset.lineItem,
            currentQty,
            newQty
        });

        // Clear any existing timer
        clearTimeout(this.quantityUpdateTimer);
        
        // Set new timer to update cart after delay
        this.quantityUpdateTimer = setTimeout(() => {
            this.queueQuantityChange(item, newQty);
        }, 300);
    }

    handleCartItemChange(e) {
        if (!e.target.classList.contains('quantity__input')) return;

        const item = e.target.closest('.cart-item');
        if (!item) return;

        const newQty = Math.max(0, parseInt(e.target.value) || 0);
        e.target.value = newQty; // Ensure display shows valid number
        item.dataset.quantity = newQty;

        console.log('Quantity input changed:', {
            itemId: item.dataset.lineItem,
            newValue: newQty
        });

        // Clear any existing timer
        clearTimeout(this.quantityUpdateTimer);
        
        // Set new timer to update cart after delay
        this.quantityUpdateTimer = setTimeout(() => {
            this.queueQuantityChange(item, newQty);
        }, 600);
    }

    handleOutsideClick(e) {
        if (!this.body.classList.contains('cart-open')) return;
        
        const isOutsideClick = !this.drawer.contains(e.target) && 
                             !e.target.closest('a[href="/cart"]');
        
        if (isOutsideClick) {
            this.closeDrawer();
        }
    }

    queueQuantityChange(item, quantity) {
        const embroideryId = item.dataset.embroideryId;
        const changes = [];
        
        if (embroideryId) {
            // Find all items with same embroideryId and add to changes
            const relatedItems = Array.from(document.querySelectorAll(`[data-embroidery-id="${embroideryId}"]`));
            relatedItems.sort((a, b) => parseInt(a.dataset.lineItem) - parseInt(b.dataset.lineItem));
            
            // Process items in reverse order when removing (quantity = 0)
            if (quantity === 0) {
                relatedItems.reverse();
            }
            
            relatedItems.forEach((relatedItem, index) => {
                changes.push({
                    line: parseInt(relatedItem.dataset.lineItem),
                    quantity: quantity
                });
            });
        } else {
            changes.push({
                line: parseInt(item.dataset.lineItem),
                quantity: quantity
            });
        }

        changes.forEach(change => this.changeQueue.add(JSON.stringify(change)));
        this.scheduleChange();
        
        console.log('Changes queued:', changes);
    }

    scheduleChange() {
        clearTimeout(this.changeDebounceTimer);
        this.changeDebounceTimer = setTimeout(() => this.processChangeQueue(), 500);
    }

    async processChangeQueue() {
        if (this.isUpdating || this.changeQueue.size === 0) return;
        
        this.isUpdating = true;
        this.transitionOut();

        try {
            console.log('Processing change queue:', this.changeQueue);

            const changes = Array.from(this.changeQueue)
                .map(json => JSON.parse(json))
                .sort((a, b) => b.line - a.line); // Process higher line numbers first
            
            // Process each change sequentially
            for (const change of changes) {
                await this.changeCartItem(change);
            }
            
            await this.refreshCart();
            this.changeQueue.clear();
            
        } catch (error) {
            console.error('Error processing changes:', error);
            this.transitionIn();
        } finally {
            this.isUpdating = false;
        }
    }

    async changeCartItem(change) {
        console.log('Changing cart item:', change);

        try {
            const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(change)
            });

            if (!response.ok) {
                throw new Error('Cart change failed');
            }

            const result = await response.json();
            console.log('Cart change completed:', result);
            return result;
        } catch (error) {
            console.error('Failed to change cart:', error);
            throw error;
        }
    }

    async refreshCart() {
        try {
            console.log('Refreshing cart display');
            const [htmlResponse, cartData] = await Promise.all([
                fetch(window.Shopify.routes.root + 'cart?view=raw').then(r => r.text()),
                fetch(window.Shopify.routes.root + 'cart.js').then(r => r.json())
            ]);

            this.updateCartHTML(htmlResponse);
            this.updateCartCounts(cartData.item_count);
            
            const subtotalElement = document.querySelector('.subtotal');
            if (subtotalElement) {
                const formattedPrice = window.Shopify.formatMoney(
                    cartData.total_price,
                    window.theme.moneyFormat || window.theme.money_format
                );
                subtotalElement.innerHTML = formattedPrice;
            }
            
            console.log('Cart refresh complete:', {
                itemCount: cartData.item_count,
                total: cartData.total_price
            });

        } catch (error) {
            console.error('Error refreshing cart:', error);
        } finally {
            this.transitionIn();
        }
    }

    updateCartHTML(html) {
        if (!this.cartItems) {
            this.cartItems = document.createElement('div');
            this.cartItems.className = 'cart-items';
            this.drawerContent.appendChild(this.cartItems);
        }
        this.cartItems.innerHTML = html;
    }

    updateCartCounts(count) {
        document.querySelectorAll('[data-cart-count]')
            .forEach(el => el.setAttribute('data-cart-count', count));
    }

    openDrawer() {
        console.log('Opening cart drawer');
        this.body.classList.add('cart-open');
        this.drawer.setAttribute('aria-hidden', 'false');
    }

    closeDrawer() {
        console.log('Closing cart drawer');
        this.body.classList.remove('cart-open');
        this.drawer.setAttribute('aria-hidden', 'true');
    }

    transitionOut() {
        if (this.cartItems) {
            this.cartItems.style.opacity = '0.2';
        }
    }

    transitionIn() {
        if (this.cartItems) {
            this.cartItems.style.opacity = '1';
        }
    }
}

// Initialize MiniCart
let miniCart;

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        console.log('Initializing MiniCart on DOMContentLoaded');
        miniCart = new MiniCart();
    });
} else {
    console.log('Initializing MiniCart immediately');
    miniCart = new MiniCart();
}

window.miniCart = miniCart;